import {
  type LearningCourseEntity,
  LearningCourseInputSchema,
  LearningCourseSchema
} from '@/core/infrastructure/schemas/learning/course/LearningCourseSchema'
import { LearningModule } from '@/core/domain/learning/module/LearningModule'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'
import type { LearningCourseInputEntity } from '@/core/infrastructure/schemas/learning/course/LearningCourseInputSchema.ts'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import { ReferentialDomain } from '@/core/domain/referential/domain/ReferentialDomain.ts'

export class LearningCourse {
  id: number
  title: string
  status: LearningStatus.Values
  order: number
  name: string
  nameSlug: string
  domain: ReferentialDomain
  learningModules: LearningModule[]
  createdBy?: string
  createdAt?: Date
  modifiedBy?: string
  modifiedAt?: Date

  constructor(course: LearningCourseEntity) {
    this.id = course.id
    this.title = course.title
    this.status = course.status
    this.order = course.order
    this.name = course.name
    this.nameSlug = course.nameSlug
    this.domain = ReferentialDomain.create(course.domain)

    // Conversion des modules
    this.learningModules = course.learningModules.map((module) => LearningModule.create(module))

    this.createdBy = course.createdBy

    // Conversion des dates si elles existent
    if (course.createdAt) {
      this.createdAt = new Date(course.createdAt)
    }

    this.modifiedBy = course.modifiedBy

    if (course.modifiedAt) {
      this.modifiedAt = new Date(course.modifiedAt)
    }
  }

  // Retourne le nombre de modules du cours
  modulesCount(): number {
    return this.learningModules.length
  }

  // Retourne les modules actifs du cours
  get activeModules(): LearningModule[] {
    return this.learningModules.filter((module) => module.status === LearningStatus.Values.ACTIVE)
  }

  // Vérifie si le cours est valide
  isValid(): boolean {
    return this.title !== '' && this.domain !== null && this.activeModules.length > 0
  }

  // Vérifie si le cours est actif
  isActive(): boolean {
    return this.status === LearningStatus.Values.ACTIVE
  }

  // Obtenir les modules triés par ordre
  getSortedModules(): LearningModule[] {
    return [...this.learningModules].sort((a, b) => a.order - b.order)
  }

  public static create(data: unknown): LearningCourse {
    return EntityValidator.validateAndCreate(
      LearningCourseSchema,
      data,
      LearningCourse,
      'LearningCourse'
    )
  }
}

export class LearningCourseInput {
  title: string
  status: LearningStatus.Values
  order: number
  name: string
  domainId?: number

  constructor(data: LearningCourseInputEntity | LearningCourseEntity) {
    this.title = data.title
    this.status = data.status
    this.order = data.order
    this.name = data.name
    if (typeof data === 'object' && 'domainId' in data) {
      this.domainId = data.domainId
    } else if (typeof data === 'object' && 'domain' in data) {
      this.domainId = data.domain.id
    }
  }

  validateForAPI() {
    if (!this.title) {
      throw new NabooError({
        message: 'Title is required',
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR'
      })
    }
    if (!this.status) {
      throw new NabooError({
        message: 'Status is required',
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR'
      })
    }
    if (this.order < 0) {
      throw new NabooError({
        message: 'Order must be a positive number',
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR'
      })
    }
    if (!this.name) {
      throw new NabooError({
        message: 'Name is required',
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR'
      })
    }
    if (this.domainId !== undefined && this.domainId < 0) {
      throw new NabooError({
        message: 'Domain ID must be a positive number',
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR'
      })
    }

    EntityValidator.validate(LearningCourseInputSchema, this, 'LearningCourseInput')

    return true
  }

  public static create(data: unknown): LearningCourseInput {
    return EntityValidator.validateAndCreate(
      LearningCourseInputSchema,
      data,
      LearningCourseInput,
      'LearningCourseInput'
    )
  }
}
